import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { CONTROL_KINDS, isControlKind } from '../lib/group-owner.js';
import type {
  CoherenceAlertMetrics,
  CoherenceControlMetrics,
  ControlChecks,
  ControlExecution,
  ControlGroup,
  ControlKind,
  ControlLaunchParameters,
  ControlPendingVideo,
  GestureControlMetrics,
  GestureMatrix,
  GestureMatrixCell,
  GestureMatrixKind,
  GestureMatrixVideo,
  GroupRunResult,
  GroupSourceType,
  MethodDelta,
  RunStatus,
} from '../model.js';
import {
  type LatestParametersPayload,
  type ReanalysisPayload,
  type RunResultPayload,
  toLatestParameters,
  toReanalysis,
  toRunResult,
} from './analysis-groups.api.js';

/** The product owner's order, which is also the order of the cards (`lib/group-owner.ts`). */
export { CONTROL_KINDS };

interface ChecksPayload {
  members?: number | null;
  manual_members?: number | null;
  with_truth_label?: number | null;
  without_truth_label?: number | null;
  with_gesture_labels?: number | null;
  gesture_labelling_complete?: number | null;
  gesture_labels?: number | null;
  with_transcription?: number | null;
}

interface ExecutionPayload {
  batch: string;
  finished_at?: string | null;
  engines?: string[] | null;
  gesture_source?: string | null;
  engine_versions?: Record<string, string> | null;
  threshold_pct?: number | null;
}

interface MetricsPayload {
  detection_pct?: number | null;
  false_positive_pct?: number | null;
  correct_videos_pct?: number | null;
  labels?: number | null;
  labels_detected?: number | null;
  detections?: number | null;
  false_positives?: number | null;
  videos_evaluated?: number | null;
  method?: string | null;
  incoherences_detected_pct?: number | null;
  false_positives_pct?: number | null;
  correct_pct?: number | null;
  incoherences_detected_all_videos_pct?: number | null;
  false_positives_all_videos_pct?: number | null;
  methods?: Record<string, MethodPayload> | null;
}

/** Veracidad's alert figures (2026-10-20): the alerts against the lie ranges, per video. */
interface CoherenceAlertsPayload {
  incoherences_detected_pct?: number | null;
  false_positives_pct?: number | null;
  correct_pct?: number | null;
  videos_evaluated?: number | null;
  videos_with_incoherence?: number | null;
  incoherents_detected?: number | null;
  videos_truthful?: number | null;
  truthful_flagged?: number | null;
  correct_videos?: number | null;
  alerts?: number | null;
  alerts_in_ranges?: number | null;
  alerts_outside_ranges?: number | null;
}

interface MethodPayload {
  incoherences_detected_pct?: number | null;
  false_positives_pct?: number | null;
  correct_pct?: number | null;
  incoherences_detected_all_videos_pct?: number | null;
  false_positives_all_videos_pct?: number | null;
}

function toMethodDelta(payload: MethodPayload): MethodDelta {
  return {
    incoherencesDetectedPct: pct(payload.incoherences_detected_pct),
    falsePositivesPct: pct(payload.false_positives_pct),
    correctPct: pct(payload.correct_pct),
    incoherencesDetectedAllVideosPct: pct(payload.incoherences_detected_all_videos_pct),
    falsePositivesAllVideosPct: pct(payload.false_positives_all_videos_pct),
  };
}

/**
 * A control as upstream serves it (contract 2026-10-28): no `group` block any more — the control is
 * not a group — but the fields a group answer has for its videos and executions, with the same
 * names and shapes, so they are read with the same builders.
 */
interface ControlPayload {
  kind: string;
  name?: string | null;
  description?: string | null;
  members?: number | null;
  members_changed_at?: string | null;
  reanalysis?: ReanalysisPayload | null;
  runs_by_status?: Partial<Record<RunStatus, number>> | null;
  executions_waiting?: number | null;
  latest_parameters?: LatestParametersPayload | null;
  checks?: ChecksPayload | null;
  warnings?: string[] | null;
  latest_execution?: ExecutionPayload | null;
  metrics?: MetricsPayload | null;
  alert_metrics?: CoherenceAlertsPayload | null;
  ready?: boolean | null;
  pending?: number | null;
}

interface PendingPayload {
  source_type?: string | null;
  source_id: number | string;
  name?: string | null;
  missing?: string[] | null;
}

interface MatrixCellPayload {
  gesture: string;
  labels?: number | null;
  labels_detected?: number | null;
  detections?: number | null;
  false_positives?: number | null;
}

interface MatrixVideoPayload {
  source_type?: string | null;
  source_id: number | string;
  name?: string | null;
  evaluated?: boolean | null;
  not_evaluated_reason?: string | null;
  labels?: number | null;
  labels_detected?: number | null;
  detections?: number | null;
  false_positives?: number | null;
  correct?: boolean | null;
  cells?: MatrixCellPayload[] | null;
  /** Absent from a gateway older than 2026-10-29: never deleted. */
  source_deleted?: boolean | null;
}

interface MatrixPayload {
  kind?: string | null;
  batch?: string | null;
  gestures?: string[] | null;
  videos?: MatrixVideoPayload[] | null;
  totals?: MetricsPayload | null;
}

function count(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Null stays null: a percentage with no denominator was not measured, which is not 0 %. */
function pct(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toChecks(payload: ChecksPayload | null | undefined): ControlChecks {
  const checks = payload ?? {};

  return {
    members: count(checks.members),
    manualMembers: count(checks.manual_members),
    withTruthLabel: count(checks.with_truth_label),
    withoutTruthLabel: count(checks.without_truth_label),
    withGestureLabels: count(checks.with_gesture_labels),
    gestureLabellingComplete: count(checks.gesture_labelling_complete),
    gestureLabels: count(checks.gesture_labels),
    withTranscription: count(checks.with_transcription),
  };
}

function toExecution(payload: ExecutionPayload | null | undefined): ControlExecution | null {
  if (!payload) return null;

  return {
    batch: payload.batch,
    finishedAt: payload.finished_at ? new Date(payload.finished_at) : null,
    engines: [...(payload.engines ?? [])],
    gestureSource: payload.gesture_source ?? 'engine',
    engineVersions: payload.engine_versions ? { ...payload.engine_versions } : null,
    thresholdPct: pct(payload.threshold_pct),
  };
}

function toGestureMetrics(payload: MetricsPayload): GestureControlMetrics {
  return {
    kind: 'gestures',
    detectionPct: pct(payload.detection_pct),
    falsePositivePct: pct(payload.false_positive_pct),
    correctVideosPct: pct(payload.correct_videos_pct),
    labels: count(payload.labels),
    labelsDetected: count(payload.labels_detected),
    detections: count(payload.detections),
    falsePositives: count(payload.false_positives),
    videosEvaluated: count(payload.videos_evaluated),
  };
}

function toCoherenceMetrics(payload: MetricsPayload): CoherenceControlMetrics {
  const method = payload.method ?? 'alert_major';
  // An API older than `methods` has only the flat method's figures.
  const methods = payload.methods
    ? Object.fromEntries(
        Object.entries(payload.methods).map(([key, block]) => [key, toMethodDelta(block)]),
      )
    : { [method]: toMethodDelta(payload) };

  return {
    kind: 'coherence',
    method,
    methods,
    incoherencesDetectedPct: pct(payload.incoherences_detected_pct),
    falsePositivesPct: pct(payload.false_positives_pct),
    correctPct: pct(payload.correct_pct),
    incoherencesDetectedAllVideosPct: pct(payload.incoherences_detected_all_videos_pct),
    falsePositivesAllVideosPct: pct(payload.false_positives_all_videos_pct),
  };
}

function toCoherenceAlertMetrics(payload: CoherenceAlertsPayload): CoherenceAlertMetrics {
  return {
    kind: 'coherence',
    incoherencesDetectedPct: pct(payload.incoherences_detected_pct),
    falsePositivesPct: pct(payload.false_positives_pct),
    correctPct: pct(payload.correct_pct),
    videosEvaluated: count(payload.videos_evaluated),
    videosWithIncoherence: count(payload.videos_with_incoherence),
    incoherentsDetected: count(payload.incoherents_detected),
    videosTruthful: count(payload.videos_truthful),
    truthfulFlagged: count(payload.truthful_flagged),
    correctVideos: count(payload.correct_videos),
    alerts: count(payload.alerts),
    alertsInRanges: count(payload.alerts_in_ranges),
    alertsOutsideRanges: count(payload.alerts_outside_ranges),
  };
}

/**
 * Veracidad's alone: its alerts against the lie ranges. Every other kind reads none, whatever the
 * body says — the alerts have a control of their own since 2026-10-29.
 */
function toAlertMetrics(
  kind: ControlKind,
  payload: ControlPayload['alert_metrics'],
): ControlGroup['alertMetrics'] {
  if (!payload || kind !== 'coherence') return null;

  return toCoherenceAlertMetrics(payload);
}

/**
 * The figures are the kind's: detection for gestures — and for gesture alerts, the same figures
 * over the alerts (2026-10-29) —, the batch headlines for the other two. Read as `kind` says, not as the body does — the kind asked for is
 * the card it lands on.
 */
function toControl(kind: ControlKind, payload: ControlPayload): ControlGroup {
  const metrics = payload.metrics ?? null;

  return {
    kind,
    // Upstream's title, or the kind itself: the screens word it from the kind anyway.
    name: payload.name ?? kind,
    description: payload.description ?? null,
    members: count(payload.members),
    membersChangedAt: payload.members_changed_at ? new Date(payload.members_changed_at) : null,
    reanalysis: toReanalysis(payload.reanalysis),
    runsByStatus: payload.runs_by_status ?? {},
    executionsWaiting: count(payload.executions_waiting),
    latestParameters: toLatestParameters(payload.latest_parameters),
    checks: toChecks(payload.checks),
    warnings: [...(payload.warnings ?? [])],
    latestExecution: toExecution(payload.latest_execution),
    metrics: metrics ? toMetrics(kind, metrics) : null,
    alertMetrics: toAlertMetrics(kind, payload.alert_metrics),
    // Not ready unless upstream says so: a card must not call a group ready that nobody checked.
    ready: payload.ready === true,
    pending: count(payload.pending),
  };
}

function toMetrics(kind: ControlKind, payload: MetricsPayload): ControlGroup['metrics'] {
  // Alertas gestuales has Gestos's figures in Gestos's shape, counted over the alerts.
  if (kind === 'gestures' || kind === 'gesture_alerts') return toGestureMetrics(payload);
  return toCoherenceMetrics(payload);
}

/** A video's figure, kept null when absent: a video left out has none, and none is not 0. */
function figure(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toMatrixCell(payload: MatrixCellPayload): GestureMatrixCell {
  return {
    gesture: payload.gesture,
    labels: count(payload.labels),
    labelsDetected: count(payload.labels_detected),
    detections: count(payload.detections),
    falsePositives: count(payload.false_positives),
  };
}

/** Not evaluated unless upstream says so: a row must not pass for one it measured. */
function toMatrixVideo(payload: MatrixVideoPayload): GestureMatrixVideo {
  return {
    sourceType: 'manual_analysis',
    sourceId: Number(payload.source_id),
    name: payload.name ?? null,
    evaluated: payload.evaluated === true,
    notEvaluatedReason: payload.not_evaluated_reason ?? null,
    labels: figure(payload.labels),
    labelsDetected: figure(payload.labels_detected),
    detections: figure(payload.detections),
    falsePositives: figure(payload.false_positives),
    correct: typeof payload.correct === 'boolean' ? payload.correct : null,
    cells: (payload.cells ?? []).map(toMatrixCell),
    sourceDeleted: payload.source_deleted === true,
  };
}

/** Read as `kind` says, as a control group is: the kind asked for is the map it lands on. */
function toMatrix(kind: GestureMatrixKind, payload: MatrixPayload): GestureMatrix {
  return {
    kind,
    batch: payload.batch ?? null,
    gestures: [...(payload.gestures ?? [])],
    videos: (payload.videos ?? []).map(toMatrixVideo),
    totals: toGestureMetrics(payload.totals ?? {}),
  };
}

/** Every video of a control group is a video: an answer that does not say so is read as one. */
function toPending(payload: PendingPayload): ControlPendingVideo {
  const sourceType: GroupSourceType =
    payload.source_type === 'interview' ? 'interview' : 'manual_analysis';
  const sourceId = Number(payload.source_id);

  return {
    sourceType,
    sourceId,
    name: payload.name ?? `Vídeo #${sourceId}`,
    missing: [...(payload.missing ?? [])],
  };
}

/**
 * Boundary with the control groups (product owner, 2026-10-06; apart from the groups since
 * 2026-10-28): four controls that live in code, nobody creates or deletes, one per kind, each with
 * what upstream checked about it, its figures and whether it is ready to be measured.
 *
 * Nothing is chosen here: which control measures what is the kind, and so are the engines a control
 * run launches with. Its videos, executions and measurements are read and changed through
 * `analysisGroupsApi` with the control as owner (`{ controlKind }`), on the kind's own routes.
 */
export const controlGroupsApi = {
  /** The four, in their order: the screen draws a card for each. */
  list: async (): Promise<ControlGroup[]> => {
    const answer = await httpClient.get<{ items?: ControlPayload[] | null }>(
      '/virtual-profiler/control-groups',
    );
    const byKind = new Map<ControlKind, ControlPayload>();

    for (const payload of answer?.items ?? []) {
      if (isControlKind(payload.kind) && !byKind.has(payload.kind))
        byKind.set(payload.kind, payload);
    }

    return CONTROL_KINDS.flatMap((kind) => {
      const payload = byKind.get(kind);

      return payload ? [toControl(kind, payload)] : [];
    });
  },

  get: async (kind: ControlKind): Promise<ControlGroup> =>
    toControl(
      kind,
      await httpClient.get<ControlPayload>(`/virtual-profiler/control-groups/${kind}`),
    ),

  /** Rewrites what it is for: the one thing of a control somebody can write. */
  update: async (kind: ControlKind, draft: { description: string | null }): Promise<ControlGroup> =>
    toControl(
      kind,
      await httpClient.put<ControlPayload>(`/virtual-profiler/control-groups/${kind}`, {
        description: draft.description,
      }),
    ),

  /** Its videos that lack something its kind measures with, each with what it lacks. */
  pending: async (kind: ControlKind): Promise<ControlPendingVideo[]> => {
    const answer = await httpClient.get<{ items?: PendingPayload[] | null }>(
      `/virtual-profiler/control-groups/${kind}/pending`,
    );

    return (answer?.items ?? []).map(toPending);
  },

  /**
   * Its gestures figures of one execution, by video and gesture: the execution named, or the kind's
   * latest (`latestExecution`). Only Gestos and Semántica read the labels, so only they have one.
   * An execution the group never ran answers `VP_GROUP_BATCH_NOT_FOUND`.
   */
  matrix: async (kind: GestureMatrixKind, batch?: string | null): Promise<GestureMatrix> =>
    toMatrix(
      kind,
      await httpClient.get<MatrixPayload>(
        `/virtual-profiler/control-groups/${kind}/gesture-matrix${batch ? `?batch=${encodeURIComponent(batch)}` : ''}`,
      ),
    ),

  /**
   * Launches an execution of the control: what a group's run answers, refused the same way, its
   * rows naming the control (`control_kind`) and no group. Only the configuration, its version and the threshold travel — the engines and where the
   * gestures come from are the kind's, upstream, so the execution always fits it.
   */
  run: async (kind: ControlKind, parameters: ControlLaunchParameters): Promise<GroupRunResult> =>
    toRunResult(
      await httpClient.post<RunResultPayload>(`/virtual-profiler/control-groups/${kind}/run`, {
        analysisConfigId: parameters.analysisConfigId,
        // Left out rather than sent empty: absent is «its current version».
        ...(parameters.configVersionId ? { configVersionId: parameters.configVersionId } : {}),
        thresholdPct: parameters.thresholdPct,
      }),
    ),
};
